import React, { useState, useCallback } from "react";
import Highlight from "react-highlighter";
import { connect } from "react-redux";
import { Link } from "react-router-dom";

import { bindActionCreators, Dispatch } from "redux";

import { DataTable, Boolean, SelectionMode, DataTablePlugins, StickyHeaderRow } from "@plex/react-components";

import { commonMessages } from "infrastructure/localization";
import { useLocalizedValues } from "ui/hooks";
import { displayMessages } from "ui/screens/translation-managers/translation-managers.localization";

import { messagesActions } from "../Messages.actions";
import { IMessageItem, IMessagesState } from "../Messages.state";

import styles from "./MessagesGrid.module.scss";

export interface IMessagesGridProps {
  messageFilter?: string;
  textFilter?: string;
  messages: IMessageItem[];
  displayTokens?: boolean;
  selectMessages: (selected: string) => void;
}

const MessagesGrid: React.FC<IMessagesGridProps> = ({ messageFilter, textFilter, messages, displayTokens, selectMessages }) => {
  const hasData = messages && messages.length > 0;
  const [selected] = useState([]);
  const keySelector = useCallback((row: IMessageItem) => row.messageId, []);

  const onSelection = items => {
    selectMessages(items[0] ? items[0].messageId : "");
  };

  const localizedValues = useLocalizedValues(() => ({
    refineSearch: commonMessages.info("refineSearch"),
    messageIdProperty: displayMessages.property("messageId"),
    textProperty: displayMessages.property("text"),
    tokenProperty: displayMessages.property("token"),
    tokenDescriptionProperty: displayMessages.property("tokenDescription"),
    parentProperty: displayMessages.property("parent"),
    statusProperty: displayMessages.property("status")
  }));

  return (
    <DataTable<IMessageItem>
      keySelector={keySelector}
      data={messages}
      plugins={[
        DataTablePlugins.Selections<IMessageItem>({
          selected,
          onSelectionChanged: onSelection,
          mode: SelectionMode.single
        }),
        DataTablePlugins.Loader()
      ]}
      className={styles.messagesGrid}
    >
      {!hasData && <DataTable.Caption captionText={localizedValues.refineSearch} />}
      <DataTable.Header>
        <StickyHeaderRow>
          <DataTable.HeaderCell key="1">{localizedValues.messageIdProperty}</DataTable.HeaderCell>
          <DataTable.HeaderCell key="2">{localizedValues.textProperty}</DataTable.HeaderCell>
          {displayTokens && <DataTable.HeaderCell key="3">{localizedValues.tokenProperty}</DataTable.HeaderCell>}
          {displayTokens && <DataTable.HeaderCell key="4">{localizedValues.tokenDescriptionProperty}</DataTable.HeaderCell>}
          <DataTable.HeaderCell key="5">{localizedValues.parentProperty}</DataTable.HeaderCell>
        </StickyHeaderRow>
      </DataTable.Header>
      <DataTable.Body<IMessageItem>>
        {(row, rowIndex, rowInfo) => {
          const toggleSelection = rowInfo && rowInfo.selectionState.toggle;
          const isSelected = rowInfo && rowInfo.selectionState.selected;
          const key = rowInfo && rowInfo.key;

          return (
            <DataTable.Row selectable selected={isSelected} key={key}>
              <DataTable.Cell onClick={toggleSelection}>
                <Link to={`/message-details/${row.messageId}`}>
                  <Highlight search={messageFilter || ""}>{row.messageId}</Highlight>
                </Link>
              </DataTable.Cell>

              <DataTable.Cell onClick={toggleSelection}>
                <Highlight search={textFilter || ""}>{row.text}</Highlight>
              </DataTable.Cell>

              {displayTokens && (
                <DataTable.Cell onClick={toggleSelection} className={styles.tokenCell}>
                  {row.tokens.map((item, index) => {
                    return (
                      <div className={styles.token} key={index}>
                        {item.name}
                      </div>
                    );
                  })}
                </DataTable.Cell>
              )}

              {displayTokens && (
                <DataTable.Cell onClick={toggleSelection} className={styles.tokenCell}>
                  {row.tokens.map((item, index) => {
                    return (
                      <div className={styles.token} key={index}>
                        {item.description}
                      </div>
                    );
                  })}
                </DataTable.Cell>
              )}
              <DataTable.Cell align="center" onClick={toggleSelection}>
                <Boolean value={row.isParentMessage} />
              </DataTable.Cell>
            </DataTable.Row>
          );
        }}
      </DataTable.Body>
    </DataTable>
  );
};

export default connect(
  (state: { messages: IMessagesState }) => {
    return {
      messageFilter: state.messages.searchQuery.messageId,
      textFilter: state.messages.searchQuery.text,
      messages: state.messages.grid.gridData,
      displayTokens: state.messages.searchQuery.displayTokens,
      selectedMessageId: state.messages.grid.selected
    };
  },
  (dispatch: Dispatch) => {
    return bindActionCreators(
      {
        selectMessages: messagesActions.toggleSelection
      },
      dispatch
    );
  }
)(MessagesGrid);
